import clsx from 'clsx';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useEscapeKey,
  useFocusTrapHandler,
  useOnClickOutside,
  usePrevious,
} from 'shared/lib/utils';
import { localStorageService } from 'shared/lib/local-storage';
import { eventService, getPage, XApiEventKeys } from 'shared/api/event-service';
import { Spinner } from 'shared/ui/spinner';
import { Portal } from 'shared/ui/portal';
import { getAmpConfigFlags } from 'shared/config/amp-config';
import {
  RESPONSIVE_BIG_MOBILE,
  RESPONSIVE_SMALL_MOBILE,
  RESPONSIVE_TABLET,
  useScreenWidth,
} from 'features/responsive';

import { useUserProgramsData } from '../model';
import { getProgramsListHeight } from '../lib';
import {
  PROGRAM_PREFERENCES_LS_KEY,
  PROGRAMS_ITEMS_GRID_GAP,
  PROGRAMS_LIST_BOTTOM_PADDING,
} from '../constants';

import { ProgramsItem } from './programs-item';
import styles from './styles.module.scss';

// @TODO: remove isProgramPreferencesEnabled checks after RFS2025
export const Programs = () => {
  const { t } = useTranslation();

  const programsItemRef = useRef<HTMLLIElement>(null);
  const programsListRef = useRef<HTMLUListElement>(null);
  const programsWrapperRef = useRef<HTMLDivElement>(null);

  const { userProgramsData, isLoading } = useUserProgramsData();
  const width = useScreenWidth();
  const isTabletLayout = width <= RESPONSIVE_TABLET;
  const isBigMobileLayout = width < RESPONSIVE_BIG_MOBILE;
  const isSmallMobileLayout = width < RESPONSIVE_SMALL_MOBILE;

  const prevIsMobileLayout = usePrevious(isTabletLayout);

  const { isProgramPreferencesEnabled } = getAmpConfigFlags();

  const [isProgramsListExpanded, setIsProgramsListExpanded] =
    useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [programPreferences, setProgramPreferences] = useState<string[]>([]);
  const [programPreferencesDraft, setProgramPreferencesDraft] = useState<
    string[]
  >([]);

  const isItemChecked = (id: string) => {
    return programPreferencesDraft.includes(id);
  };

  const isItemCheckboxDisabled = (id: string) => {
    return isItemChecked(id) && programPreferencesDraft.length === 1;
  };

  const isEditButtonVisible =
    isProgramPreferencesEnabled &&
    !isLoading &&
    userProgramsData.length > 1 &&
    !isEditMode;

  const focusTrapHandler = useFocusTrapHandler(
    programsWrapperRef,
    'input, button',
  );

  const programsWrapperKeydownHandler =
    isProgramPreferencesEnabled && isEditMode ? focusTrapHandler : undefined;

  const getMaxVisibleProgramsCount = () => {
    if (isSmallMobileLayout) return 1;

    if (isBigMobileLayout) return 2;

    if (isTabletLayout) return 3;

    return 3;
  };

  const maxVisibleProgramsCount = getMaxVisibleProgramsCount();

  const isProgramsShowLimitReached = () => {
    const displayedPrograms =
      isProgramPreferencesEnabled && programPreferences.length
        ? programPreferences.length
        : userProgramsData.length;

    return displayedPrograms > maxVisibleProgramsCount;
  };

  const getItemsInRowCount = () => {
    if (isSmallMobileLayout) return 1;

    if (isBigMobileLayout) return 2;

    if (isTabletLayout) return 3;

    return 1;
  };

  const handleProgramsListHeightSetting = (
    programsList: HTMLUListElement,
    programsItemsNumber: number,
  ) => {
    const itemsInRowCount = getItemsInRowCount();
    const programsListGridGap = isTabletLayout ? PROGRAMS_ITEMS_GRID_GAP : 0;

    if (isProgramPreferencesEnabled) {
      programsList.style.maxHeight = 'max-content';
    } else {
      programsList.style.maxHeight = `${getProgramsListHeight({
        programsItemRef,
        listBottomPadding: PROGRAMS_LIST_BOTTOM_PADDING,
        itemsInRowCount,
        programsListGridGap,
        programsItemsNumber,
      })}px`;
    }
  };

  const expandProgramsList = () => {
    if (programsListRef.current) {
      handleProgramsListHeightSetting(
        programsListRef.current,
        userProgramsData.length,
      );

      setIsProgramsListExpanded(true);
    }
  };

  const collapseProgramsList = () => {
    if (programsListRef.current) {
      programsListRef.current.style.maxHeight = '';
      setIsProgramsListExpanded(false);
    }
  };

  const handleExpandButtonClick = (e: MouseEvent) => {
    if (isProgramsListExpanded) {
      collapseProgramsList();
    } else {
      expandProgramsList();
    }

    eventService.send(
      isProgramsListExpanded
        ? XApiEventKeys.programsListCollapsed
        : XApiEventKeys.programsListExpanded,
      undefined,
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          url: window.location.href,
          page: getPage(),
        },
      },
    );
  };

  const handleProgramQuickLinkFocus = (linkIndex: number) => {
    if (linkIndex >= maxVisibleProgramsCount - 1) {
      expandProgramsList();
    }
  };

  const handleEditModeEnable = () => {
    setIsEditMode(true);

    expandProgramsList();
  };

  const handleEditModeDisable = () => {
    setIsEditMode(false);

    collapseProgramsList();
  };

  const handleEditModeCancel = () => {
    handleEditModeDisable();

    setProgramPreferencesDraft(programPreferences);
  };

  const handleProgramsPreferencesSave = () => {
    setProgramPreferences(programPreferencesDraft);

    localStorageService.saveUserData(
      PROGRAM_PREFERENCES_LS_KEY,
      programPreferencesDraft,
    );

    handleEditModeDisable();
  };

  const handleCheckboxClick = (itemId: string) => {
    if (isItemCheckboxDisabled(itemId)) return;

    setProgramPreferencesDraft((currentPreferences) => {
      if (currentPreferences.includes(itemId)) {
        return currentPreferences.filter(
          (assetBusinessKey) => assetBusinessKey !== itemId,
        );
      }

      return currentPreferences.concat(itemId);
    });
  };

  const handleClickOutside = () => {
    if (isProgramPreferencesEnabled && isEditMode) return;

    collapseProgramsList();
  };

  useEscapeKey(handleEditModeCancel);
  useOnClickOutside(programsWrapperRef, handleClickOutside);

  // collapse programs list on layout change
  useEffect(() => {
    if (prevIsMobileLayout === undefined || isEditMode) return;

    const hasMobileLayoutChanged = isTabletLayout !== prevIsMobileLayout;

    if (!hasMobileLayoutChanged) return;

    collapseProgramsList();

    // prevIsMobileLayout should not be included in deps array for correct check of layout change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabletLayout, isEditMode]);

  useEffect(() => {
    const savedProgramPreferences = localStorageService.getUserData(
      PROGRAM_PREFERENCES_LS_KEY,
    );

    if (savedProgramPreferences) {
      setProgramPreferences(savedProgramPreferences);
      return;
    }

    if (isLoading) return;

    setProgramPreferences(userProgramsData.map((asset) => asset.businessKey));
  }, [userProgramsData, isLoading]);

  useEffect(() => {
    setProgramPreferencesDraft(programPreferences);
  }, [programPreferences]);

  if (!isLoading && userProgramsData.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx('w-100', styles.programsWrapper, {
        [styles.editModeContent]: isProgramPreferencesEnabled && isEditMode,
      })}
      ref={programsWrapperRef}
      id="programs_section"
      onKeyDown={programsWrapperKeydownHandler}
    >
      <div
        className={clsx(styles.header, {
          [styles.editModeHeader]: isEditMode,
        })}
      >
        <h2 className={styles.title}>{t('Your Programs')}</h2>
        {isEditButtonVisible && (
          <button
            type="button"
            className="btn btn-icon-ghost"
            onClick={handleEditModeEnable}
            aria-label="Edit program preferences"
          >
            <i className="icon-v2-edit" aria-hidden />
          </button>
        )}
        {isProgramPreferencesEnabled && isEditMode && (
          <button
            type="button"
            className={styles.editModeSaveBtn}
            onClick={handleProgramsPreferencesSave}
          >
            {t('Save')}
          </button>
        )}
      </div>
      {isProgramPreferencesEnabled && isEditMode && (
        <div className={styles.editModeCaption}>
          {t(
            'Show/Hide programs you want to have access to in the main Navigation and Home Page.',
          )}
        </div>
      )}
      {isLoading ? (
        <div className={styles.spinnerWrapper}>
          <Spinner isDark testId="programs-spinner" />
        </div>
      ) : (
        <>
          <ul
            ref={programsListRef}
            className={clsx(styles.programsList, {
              [styles.editModeOverflowDisabled]:
                isEditMode && isProgramsListExpanded,
            })}
          >
            {userProgramsData.map((programDataObj, programIndex) => {
              const isChecked = isItemChecked(programDataObj.businessKey);
              const isCheckboxDisabled = isItemCheckboxDisabled(
                programDataObj.businessKey,
              );
              const onCheckboxClick = () =>
                handleCheckboxClick(programDataObj.businessKey);

              if (
                isProgramPreferencesEnabled &&
                !isEditMode &&
                !programPreferences.includes(programDataObj.businessKey)
              )
                return null;

              return (
                <ProgramsItem
                  programsItemRef={programsItemRef}
                  key={programDataObj.businessKey}
                  programObj={programDataObj}
                  onFocus={() => handleProgramQuickLinkFocus(programIndex)}
                  isEditMode={isEditMode}
                  isChecked={isChecked}
                  onCheckboxClick={onCheckboxClick}
                  isCheckboxDisabled={isCheckboxDisabled}
                />
              );
            })}
          </ul>
          {isProgramsShowLimitReached() && !isEditMode && (
            <button
              className={clsx(
                styles.showMoreBtn,
                'd-flex',
                'align-items-center',
                'scale-minus-1',
              )}
              onClick={handleExpandButtonClick}
              onContextMenu={handleExpandButtonClick}
              onFocus={collapseProgramsList}
            >
              <span>
                {isProgramsListExpanded ? t('Show Less') : t('Show More')}
              </span>
              <i
                className={clsx('icon-v2-chevron-small-right', styles.icon, {
                  [styles.turnedUp]: isProgramsListExpanded,
                })}
                aria-hidden
              />
            </button>
          )}
        </>
      )}
      {isProgramPreferencesEnabled && isEditMode && (
        <div className={styles.editModeCloseBtnContainer}>
          <button
            className={clsx(
              'btn',
              'btn-icon-float',
              'label-top',
              styles.editModeCloseBtn,
            )}
            onClick={handleEditModeCancel}
          >
            <label className="amplify-uppercase">{t('Close')}</label>
            <i className="icon-v2-close" aria-hidden="true" />
          </button>
        </div>
      )}
      {isProgramPreferencesEnabled && isEditMode && (
        <Portal>
          <div className={styles.editModeOverlay} />
        </Portal>
      )}
    </div>
  );
};
